import classNames from 'classnames';
import Immutable from 'immutable';
import React from 'react';

import TableColumnSelector from 'sharedreact/components/TableColumnSelector';
import { RowType } from 'sharedreact/components/TableRow';
import SortOrder from 'sharedreact/data/SortOrder';
import type TableColumn from 'sharedreact/data/TableColumn';

type Props<T> = {
  tableColumns: Immutable.List<TableColumn<T>>,
  width: string,
  columnWidths: Immutable.List<string>,
  sortOrder: SortOrder,
  allTableColumns?: Immutable.List<TableColumn<T>>,
  reorderColumn?: number,
  headerRootRef?: React.LegacyRef<HTMLDivElement>,

  onColumnHeaderMouseEnter?: (columnIndex: number, event?: React.MouseEvent<HTMLTableCellElement>) => void,
  onColumnHeaderMouseLeave?: (columnIndex: number, event?: React.MouseEvent<HTMLTableCellElement>) => void,
  onColumnHeaderMouseMove?: (columnIndex: number, event?: React.MouseEvent<HTMLTableCellElement>) => void,
  onColumnHeaderMouseDown?: (columnIndex: number, event?: React.MouseEvent<HTMLTableCellElement>) => void,
  onColumnHeaderClick?: (columnIndex: number, event?: React.MouseEvent<HTMLTableCellElement>) => void,
  onToggleColumnSelection?: (columnName: string) => void,
};

type State = {
  showColumnSelectionList: boolean
};

type DefaultProps<T> = Required<Pick<Props<T>, 'allTableColumns' | 'reorderColumn'>>;

type PropsWithDefaults<T extends RowType> = Omit<Props<T>, keyof DefaultProps<T>> & DefaultProps<T>;

class TableHeader<T extends RowType> extends React.PureComponent<Props<T>, State> {
  static defaultProps: DefaultProps<unknown> = {
    allTableColumns: Immutable.List(),
    reorderColumn: -1
  };

  constructor (props: Props<T>) {
    super(props);

    this.state = {
      showColumnSelectionList: false
    };
  }

  override render (): React.ReactNode {
    const {
      tableColumns, width, columnWidths, sortOrder, allTableColumns,
      reorderColumn, onColumnHeaderMouseEnter, onColumnHeaderMouseLeave,
      onColumnHeaderMouseMove, onColumnHeaderMouseDown, onColumnHeaderClick,
      onToggleColumnSelection, headerRootRef
    } = (this.props as PropsWithDefaults<T>);

    const { showColumnSelectionList } = this.state;

    const isSortedByColumn = (tableColumn: TableColumn<T>) => {
      if (sortOrder.keyword == null || sortOrder.keyword.length === 0) {
        return false;
      }

      if (sortOrder.keyword === tableColumn.sortOrderKeyword) {
        return true;
      } else {
        return false;
      }
    };

    const renderSortedComponent = (tableColumn: TableColumn<T>) => {
      if (isSortedByColumn(tableColumn)) {
        if (sortOrder.ascending) {
          return <span key='1' className='bi bi-caret-up-fill' aria-hidden='true' />;
        } else {
          return <span key='1' className='bi bi-caret-down-fill' aria-hidden='true' />;
        }
      } else if (tableColumn.alignRight != null && tableColumn.alignRight === true) {
        return <span key='1' className='esko_tablecoltitlenotsorted' aria-hidden='true' />;
      }

      return null;
    };

    const selectableColumns: Immutable.List<TableColumn<T>> = allTableColumns.filter(tableColumn => tableColumn.selectable);

    let headerComponents: React.ReactNode[] = [];
    if (tableColumns.size > 0) {
      headerComponents = tableColumns.map((tableColumn, index) => {
        const mouseEnterHandler = onColumnHeaderMouseEnter ? (event: React.MouseEvent<HTMLTableCellElement>) => { onColumnHeaderMouseEnter(index, event); } : undefined;
        const mouseLeaveHandler = onColumnHeaderMouseLeave ? (event: React.MouseEvent<HTMLTableCellElement>) => { onColumnHeaderMouseLeave(index, event); } : undefined;
        const mouseMoveHandler = onColumnHeaderMouseMove ? (event: React.MouseEvent<HTMLTableCellElement>) => { onColumnHeaderMouseMove(index, event); } : undefined;
        const mouseDownHandler = onColumnHeaderMouseDown ? (event: React.MouseEvent<HTMLTableCellElement>) => { onColumnHeaderMouseDown(index, event); } : undefined;
        const clickHandler = onColumnHeaderClick ? (event: React.MouseEvent<HTMLTableCellElement>) => { onColumnHeaderClick(index, event); } : undefined;
  
        const title = reorderColumn === index ? ' ' : tableColumn.title;
  
        const alignRight: boolean = tableColumn.alignRight != null && tableColumn.alignRight === true;
        const alignCenter: boolean = tableColumn.alignCenter != null && tableColumn.alignCenter === true;
  
        return (
          <th key={'hcol' + index}
            style={{width: columnWidths.get(index)}}
            onMouseEnter={mouseEnterHandler}
            onMouseLeave={mouseLeaveHandler}
            onMouseMove={mouseMoveHandler}
            onMouseDown={mouseDownHandler}
            onClick={clickHandler}
          >
            <div className={classNames('esko_tablecol', {esko_tablecolsorted: isSortedByColumn(tableColumn)})}>
              <span key='0' className={classNames('esko_tablecoltitle', {esko_tablecoltitlealignright: alignRight, esko_tablecoltitlealigncenter: alignCenter})}>{title}</span>
              {reorderColumn !== index && renderSortedComponent(tableColumn)}
              {index < (tableColumns.size - 1) && <span key='2' className='esko_tablecoldivider'>{' '}</span>}
              {index === (tableColumns.size - 1) && selectableColumns.size > 0 && onToggleColumnSelection &&
                <TableColumnSelector<T>
                  key='3'
                  columns={selectableColumns}
                  selectedColumns={tableColumns}
                  showDropdown={showColumnSelectionList}
                  onToggleColumnSelection={onToggleColumnSelection}
                  onToggleDropdown={() => this.setState({showColumnSelectionList: !showColumnSelectionList})}
                />
              }
            </div>
          </th>
        );
      }).toJS();
    } else {
      headerComponents.push(
        <th key={'hcol0'} style={{width: '100%'}} >
          <div className='esko_tablecol'>
            <span className='esko_tablecoltitle'>{' '}</span>
            {selectableColumns.size > 0 && onToggleColumnSelection &&
              <TableColumnSelector<T>
                columns={selectableColumns}
                selectedColumns={tableColumns}
                showDropdown={showColumnSelectionList}
                onToggleColumnSelection={onToggleColumnSelection}
                onToggleDropdown={() => this.setState({showColumnSelectionList: !showColumnSelectionList})}
              />
            }
          </div>
        </th>
      );
    }

    return (
      <div className='esko_tableheader' style={{width: '100%'}} ref={headerRootRef}>
        <table style={{width: width}}>
          <thead>
            <tr>
              {headerComponents}
            </tr>
          </thead>
        </table>
      </div>
    );
  }
}

export default TableHeader;
